// Library Methods
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import DefaultCard from "src/components/shared/layouts/DefaultCard/DefaultCard";
import Alert from "src/components/shared/alerts/Alert/Alert";
import DeleteVideoModal from "./DeleteVideoModal/DeleteVideoModal";
import { TrashIcon } from "src/assets/icons";

// Hooks
import { useClinicVideos } from "src/services/clinics/hooks/clinic-videos/useClinicVideos";
import { useAddClinicVideo } from "src/services/clinics/hooks/clinic-videos/useAddClinicVideo";
import { useDeleteClinicVideo } from "src/services/clinics/hooks/clinic-videos/useDeleteClinicVideo";
import { useComponentDimensions } from "src/hooks/window/useComponentDimensions";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Types
import { ClinicVideoType } from "src/services/clinics/types";

// Utils
import { getYouTubeEmbedUrl } from "src/utils/text/textHelper";

type VideoSettingsType = {
  isLoadingSettings: boolean;
  isUpdatingSettings: boolean;
};

const VideoSettings = ({
  isLoadingSettings,
  isUpdatingSettings,
}: VideoSettingsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { onSurface, mutedText } = getSurfaceTokens(theme);
  const { clinicInfo } = useClinicContext();

  const [selectedVideo, setSelectedVideo] = useState<ClinicVideoType>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [videoErrors, setVideoErrors] = useState<Record<string, boolean>>({});

  const {
    data: videoData,
    isLoading: isLoadingVideos,
    isFetching: isFetchingVideos,
    isRefetching: isRefetchingVideos,
    isError: isErrorVideos,
  } = useClinicVideos(clinicInfo?.id);

  const { dynamicWidth } = useComponentDimensions({
    componentId: "#package-header",
  });

  const { mutate: addVideoMutate, isPending: addVideoPending } =
    useAddClinicVideo(clinicInfo?.id);

  const { mutate: deleteVideoMutate, isPending: deleteVideoPending } =
    useDeleteClinicVideo(clinicInfo?.id);

  const isLoading = useMemo(
    () => isLoadingSettings || isLoadingVideos,
    [isLoadingSettings, isLoadingVideos]
  );

  const isUpdating = useMemo(
    () =>
      isUpdatingSettings ||
      isFetchingVideos ||
      isRefetchingVideos ||
      addVideoPending ||
      deleteVideoPending,
    [
      isUpdatingSettings,
      isFetchingVideos,
      isRefetchingVideos,
      addVideoPending,
      deleteVideoPending,
    ]
  );

  const hasNoVideos = useMemo(
    () => !isLoadingVideos && !isErrorVideos && !videoData?.videos?.length,
    [isLoadingVideos, isErrorVideos, videoData]
  );

  const hasVideos = useMemo(
    () =>
      !isLoadingVideos &&
      !isErrorVideos &&
      videoData &&
      videoData?.videos?.length > 0,
    [isLoadingVideos, isErrorVideos, videoData]
  );

  const deleteVideo = useCallback(() => {
    if (!selectedVideo) return;

    deleteVideoMutate(selectedVideo?.id, {
      onSuccess: () => {
        setSelectedVideo(undefined);
        setOpenDeleteModal(false);
      },
    });
  }, [selectedVideo, deleteVideoMutate]);

  return (
    <>
      <Typography
        sx={{ textTransform: "uppercase" }}
        fontSize="16px"
        color={mutedText}
      >
        {t("Video Settings")}
      </Typography>

      <DefaultCard>
        <Box display="flex" flexDirection="column" gap="16px">
          <Box display="flex" flexDirection="column" gap="6px">
            <Typography fontSize="14px" fontWeight={600}>
              {t("Exam Videos")}
            </Typography>

            <Typography fontSize="14px" color={mutedText}>
              {t("Manage videos that can be played during exams.")}
            </Typography>
          </Box>

          {isLoading ? (
            <Skeleton
              variant="rounded"
              width="100%"
              height={UI_SIZES.inputHeight}
              sx={{
                borderRadius: "4px",
              }}
            />
          ) : (
            <Box display="flex" flexDirection="column" gap="20px">
              {hasNoVideos && (
                <Alert variant="warning" alertContent={t("No videos found.")} />
              )}

              <Box
                sx={{
                  display: hasVideos ? "grid" : "none",
                  gridTemplateColumns: {
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(3, 1fr)",
                    md: "repeat(5, 1fr)",
                  },
                  gap: "12px",
                }}
              >
                {hasVideos &&
                  videoData?.videos?.map((video, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            maxWidth: `calc(${dynamicWidth}px - 225px)`,
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {video.videoName}
                        </Typography>

                        <Tooltip title={t("Delete Video")}>
                          <IconButton
                            sx={{
                              color: onSurface,
                              padding: "4px",
                            }}
                            disabled={isUpdating}
                            onClick={() => {
                              setSelectedVideo(video);
                              setOpenDeleteModal(true);
                            }}
                          >
                            <TrashIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/.test(
                        video.videoUrl
                      ) ? (
                        <iframe
                          src={getYouTubeEmbedUrl(video.videoUrl)}
                          title={video.videoName}
                          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                          style={{
                            borderRadius: "8px",
                            objectFit: "cover",
                            width: "100%",
                            height: "100%",
                            minHeight: "250px",
                          }}
                        />
                      ) : (
                        <>
                          {!videoErrors[video.id] && (
                            <video
                              src={video.videoUrl}
                              controls
                              style={{
                                objectFit: "cover",
                                borderRadius: "8px",
                                width: "100%",
                                minHeight: "250px",
                                display: "block",
                              }}
                              onError={() =>
                                setVideoErrors((prev) => ({
                                  ...prev,
                                  [video.id]: true,
                                }))
                              }
                            />
                          )}
                          {videoErrors[video.id] && (
                            <Box
                              sx={{
                                backgroundColor: "#000",
                                borderRadius: "8px",
                                width: "100%",
                                minHeight: "250px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                textAlign: "center",
                              }}
                            >
                              <Typography sx={{ color: "#FFF", p: "20px" }}>
                                {t(
                                  "Video could not be loaded. Please check the URL."
                                )}
                              </Typography>
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  ))}
              </Box>

              {/* Modal to delete videos and exams */}
              <DeleteVideoModal
                selectedVideo={selectedVideo}
                open={openDeleteModal}
                onClose={() => {
                  setSelectedVideo(undefined);
                  setOpenDeleteModal(false);
                }}
                onConfirm={deleteVideo}
              />

              {/* <Button
                  disabled={isLoading || isUpdating}
                  sx={ModalCustomConfirmDark}
                  startIcon={<PlusIcon />}
                  onClick={addVideoMutate}
                >
                  {t("Add Video")}
                </Button> */}
            </Box>
          )}
        </Box>
      </DefaultCard>
    </>
  );
};

export default VideoSettings;
